import { useEffect, useMemo, useState } from 'react';
import { ArrowDown, PencilSimple, Receipt, Tag, Trash } from '@phosphor-icons/react';

import { Button } from '../ui/Button';
import { CardErrorState } from '../common/CardErrorState';
import { EmptyState } from '../common/EmptyState';
import { ExpensesSectionLabel } from './ExpensesSectionLabel';
import pt from '../../locales/pt';
import {
  dayExpenseTotal,
  formatExpenseDayHeader,
  formatExpenseTime,
  groupExpensesByDay,
} from '../../utils/expenseListUtils';
import { formatEUR } from '../../utils/money';
import { cn } from '../../utils/cn';

const PREVIEW_DAY_LIMIT = 3;
const MD_QUERY = '(min-width: 768px)';
const DEFAULT_CATEGORY_COLOR = '#7C9E6F';
/** `$group-*-glow` do `.pen` = cor da categoria a 28% (sufixo hex `47`). */
const GLOW_ALPHA = '47';

function useMdUpShowAll() {
  const [showAll, setShowAll] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(MD_QUERY).matches;
  });

  useEffect(() => {
    const mq = window.matchMedia(MD_QUERY);
    const onChange = () => {
      if (mq.matches) setShowAll(true);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return [showAll, setShowAll];
}

/**
 * Registo do dia — `.pen` `ds/row/record-item` (`Kzmdf`), grelha de 4 zonas:
 *   row     gap `$space-3` · padding `[$space-2-5, $space-4]` · center
 *   chip    32×32 · `$radius-ds-md` · fill glow da categoria
 *     icon  16 · tag · cor da categoria
 *   m       fill_container · vertical · gap 2
 *     n     `$type-body` 600 · `$fg-card`
 *     w     `$type-chip` normal · `$fg-muted`
 *   r       vertical · gap 2 · fim
 *     a     `$font-display` `$type-display-row` 600 · `$fg-card`
 *     w     `$type-chip` normal · `$fg-muted`
 *   actions gap `$space-1` · ícones 16 · `$fg-muted`
 */
function ExpenseRow({ expense, canEdit, onEdit, onDelete }) {
  const color = expense.category_color || DEFAULT_CATEGORY_COLOR;

  return (
    <li className="flex min-w-0 items-center gap-space-3 border-b border-border px-space-4 py-space-2-5 last:border-b-0">
      <span
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-ds-md"
        style={{ backgroundColor: `${color}${GLOW_ALPHA}` }}
      >
        <Tag className="h-4 w-4" style={{ color }} aria-hidden />
      </span>

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="type-body truncate font-semibold text-card-foreground">
          {expense.category_name}
        </p>
        {expense.description ? (
          <p className="type-chip truncate font-normal text-muted-foreground">
            {expense.description}
          </p>
        ) : null}
      </div>

      <div className="flex shrink-0 flex-col items-end gap-0.5">
        <p className="type-display-row tnum font-semibold text-card-foreground">
          {`− ${formatEUR(Number(expense.amount))}`}
        </p>
        <p className="type-chip tnum font-normal text-muted-foreground">
          {formatExpenseTime(expense.created_at)}
        </p>
      </div>

      {canEdit ? (
        <div className="flex shrink-0 items-center gap-space-1">
          <button
            type="button"
            onClick={() => onEdit(expense)}
            className="rounded-ds-full p-1 text-muted-foreground transition-colors hover:bg-muted"
            aria-label={pt.expenses.actions.edit}
          >
            <PencilSimple className="h-4 w-4" aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => onDelete(expense)}
            className="rounded-ds-full p-1 text-muted-foreground transition-colors hover:bg-danger-tint hover:text-danger-fg"
            aria-label={pt.expenses.actions.delete}
          >
            <Trash className="h-4 w-4" aria-hidden />
          </button>
        </div>
      ) : null}
    </li>
  );
}

/**
 * Cartão de dia — `.pen` `ds/card/record-day-group` (`LLAJN`):
 *   card  `$surface-card` · `$radius-ds-lg` · stroke `$border`
 *   head  `$surface-muted` · stroke bottom `$border` · gap `$space-3`
 *         padding `[$space-3, $space-5]` · space_between · center
 *     d   `$font-display` `$type-body-sm` 600 · `$fg-card`
 *     v   `$font-display` `$type-body-sm` 600 · `$fg-muted` · direita
 *   rows  vertical · divisores 1px `$border`
 */
function DayCard({ day, items, canEditExpense, onEdit, onDelete }) {
  const total = dayExpenseTotal(items);

  return (
    <section
      data-ds-id="ds/card/record-day-group"
      className="flex w-full min-w-0 flex-col overflow-hidden rounded-ds-lg border border-border bg-card"
    >
      <div className="flex min-w-0 items-center justify-between gap-space-3 border-b border-border bg-muted px-space-5 py-space-3">
        <h3 className="type-body-sm font-display font-semibold text-card-foreground">
          {formatExpenseDayHeader(day)}
        </h3>
        <p className="type-body-sm tnum font-display font-semibold text-muted-foreground">
          {formatEUR(total)}
        </p>
      </div>
      <ul className="flex min-w-0 flex-col">
        {items.map((expense) => (
          <ExpenseRow
            key={expense.id}
            expense={expense}
            canEdit={canEditExpense(expense)}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ))}
      </ul>
    </section>
  );
}

export function ExpensesCycleList({
  expenses = [],
  isFetching = false,
  hasNextPage = false,
  onLoadMore,
  canEditExpense,
  onEdit,
  onDelete,
  hasActiveFilters = false,
  isError = false,
  onRetry,
}) {
  const [showAll, setShowAll] = useMdUpShowAll();
  const grouped = useMemo(() => groupExpensesByDay(expenses), [expenses]);
  const visibleDays = showAll ? grouped : grouped.slice(0, PREVIEW_DAY_LIMIT);
  const hasMoreDays = grouped.length > PREVIEW_DAY_LIMIT;

  const heading = (
    <ExpensesSectionLabel
      title={pt.expenses.sections.cycleList}
      hint={pt.expenses.sections.cycleListHint}
    />
  );

  // `.pen` `ds/card/record-day-group` › `error`
  if (isError) {
    return (
      <div className="flex min-w-0 flex-col gap-space-5">
        {heading}
        <div className="w-full min-w-0 overflow-hidden rounded-ds-lg border border-border bg-card">
          <CardErrorState title={pt.expenses.list.loadError} onRetry={onRetry} />
        </div>
      </div>
    );
  }

  if (expenses.length === 0 && isFetching) {
    return (
      <div className="flex min-w-0 flex-col gap-space-5">
        {heading}
        <div
          className="flex min-h-[10rem] flex-col gap-space-3 rounded-ds-lg border border-border bg-card p-space-5"
          aria-busy="true"
          aria-label={pt.common.loading}
        >
          <div className="h-4 w-2/3 animate-pulse rounded-ds-sm bg-skeleton" />
          <div className="h-10 w-full animate-pulse rounded-ds-sm bg-skeleton" />
        </div>
      </div>
    );
  }

  if (expenses.length === 0 && !isFetching && hasActiveFilters) {
    return (
      <div className="flex min-w-0 flex-col gap-space-5">
        {heading}
        <EmptyState
          icon={Receipt}
          title={pt.expenses.filters.noResults}
          description={pt.expenses.filters.toggleHide}
        />
      </div>
    );
  }

  if (expenses.length === 0 && !isFetching) {
    return null;
  }

  return (
    <div className="flex min-w-0 flex-col gap-space-5">
      {heading}

      <div className="flex min-w-0 flex-col gap-space-2">
        {visibleDays.map(([day, items]) => (
          <DayCard
            key={day}
            day={day}
            items={items}
            canEditExpense={canEditExpense}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ))}
      </div>

      {!showAll && hasMoreDays ? (
        <div className="flex justify-center pt-space-1 md:hidden">
          <button
            type="button"
            onClick={() => setShowAll(true)}
            className={cn('type-link-sm text-sage-on-tint hover:underline')}
          >
            {pt.expenses.list.seeAllCycle}
          </button>
        </div>
      ) : null}

      {showAll && hasNextPage ? (
        <div className="flex justify-center pt-space-1">
          <Button variant="ghost" size="sm" onClick={onLoadMore} disabled={isFetching}>
            <ArrowDown className="h-4 w-4" aria-hidden />
            {isFetching ? pt.common.loading : pt.expenses.list.loadMore}
          </Button>
        </div>
      ) : null}

      {showAll && !hasNextPage && expenses.length > 0 ? (
        <p className="type-chip text-center font-normal text-muted-foreground">
          {pt.expenses.list.noMore}
        </p>
      ) : null}
    </div>
  );
}
